import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { safeGetItem, safeSetItem } from "../../lib/sosStorage";
import { drawQRCodeToCanvas } from "../../lib/sosQR";
import { useAuth } from "../../context/AuthContext";
import { useSOS } from "../../context/SOSContext";
import {
  requestPreparednessReward,
  validatePreparednessCard,
} from "../../lib/preparednessReward";

const STORAGE_KEY = "sos_identity_card";

const EMPTY_CARD = {
  name: "",
  blood_type: "",
  emergency_contact: "",
  notes: "",
};

function _loadCard() {
  const raw = safeGetItem(STORAGE_KEY, null);
  if (!raw) return EMPTY_CARD;
  try {
    const parsed = JSON.parse(raw);
    return { ...EMPTY_CARD, ...parsed };
  } catch (_e) {
    return EMPTY_CARD;
  }
}

function _buildPlainText(card) {
  const lines = [
    "QUE ONDA CHAT - TARJETA DE EMERGENCIA",
    `Nombre: ${card.name || "-"}`,
    `Tipo de sangre: ${card.blood_type || "-"}`,
    `Contacto de emergencia: ${card.emergency_contact || "-"}`,
    `Alergias / Notas: ${card.notes || "-"}`,
  ];
  return lines.join("\n");
}

export function SOSIdentityCard() {
  const { user, refreshUser } = useAuth();
  const { isOnline } = useSOS();
  const [card, setCard] = useState(_loadCard);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);

  const render = useCallback((data) => {
    if (!canvasRef.current) return;
    const text = _buildPlainText(data);
    drawQRCodeToCanvas(canvasRef.current, text, {
      size: 220,
      fg: "#0F172A",
      bg: "#FFFFFF",
      level: "M",
    });
  }, []);

  useEffect(() => {
    render(card);
    // Only on mount — subsequent re-renders happen explicitly on save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (field) => (e) => {
    setCard((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear the field-level error as soon as the user starts fixing it.
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const save = async () => {
    // 1. Validate per Fase 8 contract.
    const { ok: valid, errors: validationErrors } = validatePreparednessCard(card);
    if (!valid) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSaving(true);

    // 2. Persist locally. If quota fails even with eviction, bail with an
    //    error toast — we don't want a half-saved reward state.
    const persisted = safeSetItem(STORAGE_KEY, JSON.stringify(card));
    if (!persisted) {
      toast.error("No se pudo guardar la tarjeta (almacenamiento lleno).");
      setSaving(false);
      return;
    }
    render(card);
    toast.success("Tarjeta guardada. QR actualizado.");
    try {
      window.dispatchEvent(new Event("sos.card.saved"));
    } catch (_e) {
      /* ignore */
    }

    // 3. Reward flow — handled entirely inside requestPreparednessReward
    //    (toast rules, pending flag, idempotency notified flag). We just
    //    pass auth state and let it branch.
    try {
      await requestPreparednessReward({ user, isOnline });
      // Server response may have updated the balance — refresh user profile
      // so the Nexa chip reflects it immediately (also arrives via WS, but
      // this is belt-and-suspenders for the first-grant path).
      if (user) {
        try {
          await refreshUser?.();
        } catch (_e) {
          /* ignore */
        }
      }
    } catch (_e) {
      /* already handled inside requestPreparednessReward */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="q-glass border border-white/10 rounded-2xl p-4 sm:p-5"
      data-testid="sos-identity-card"
    >
      <h3 className="font-display text-base font-bold tracking-tight mb-1 text-slate-100">
        Tarjeta de identidad de emergencia
      </h3>
      <p className="text-[11px] text-slate-400 mb-3">
        Se guarda SOLO en este dispositivo. Nunca sale a la red. Genera un QR local.
      </p>
      <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-4">
        Ejemplo: Juan Pérez — O+ — Contacto: +507 6000-0000
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <label className="text-xs text-slate-300 flex flex-col gap-1">
          Nombre completo
          <input
            type="text"
            value={card.name}
            onChange={update("name")}
            data-testid="sos-card-name"
            className={`q-input ${errors.name ? "q-input-error" : ""}`}
            placeholder="Juan Pérez"
            autoComplete="off"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "sos-card-name-error" : undefined}
          />
          {errors.name && (
            <span
              id="sos-card-name-error"
              data-testid="sos-card-name-error"
              className="text-[11px] text-red-300 mt-0.5"
            >
              {errors.name}
            </span>
          )}
        </label>
        <label className="text-xs text-slate-300 flex flex-col gap-1">
          Tipo de sangre
          <input
            type="text"
            value={card.blood_type}
            onChange={update("blood_type")}
            data-testid="sos-card-blood"
            className="q-input"
            placeholder="O+"
            autoComplete="off"
          />
        </label>
        <label className="text-xs text-slate-300 flex flex-col gap-1 sm:col-span-2">
          Contacto de emergencia
          <input
            type="text"
            value={card.emergency_contact}
            onChange={update("emergency_contact")}
            data-testid="sos-card-contact"
            className={`q-input ${errors.emergency_contact ? "q-input-error" : ""}`}
            placeholder="+507 6000-0000"
            autoComplete="off"
            inputMode="tel"
            aria-invalid={!!errors.emergency_contact}
            aria-describedby={errors.emergency_contact ? "sos-card-contact-error" : undefined}
          />
          {errors.emergency_contact && (
            <span
              id="sos-card-contact-error"
              data-testid="sos-card-contact-error"
              className="text-[11px] text-red-300 mt-0.5"
            >
              {errors.emergency_contact}
            </span>
          )}
        </label>
        <label className="text-xs text-slate-300 flex flex-col gap-1 sm:col-span-2">
          Alergias / Notas médicas
          <textarea
            value={card.notes}
            onChange={update("notes")}
            data-testid="sos-card-notes"
            className="q-input min-h-[64px] resize-none"
            placeholder="Alergia a penicilina. Diabético."
            rows={2}
          />
        </label>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-center sm:items-start">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="sos-card-save"
          className="q-btn-primary inline-flex items-center justify-center gap-2 !py-2 w-full sm:w-auto disabled:opacity-60"
        >
          <Save size={14} /> Guardar y regenerar QR
        </button>
        <div className="flex flex-col items-center gap-1">
          <canvas
            ref={canvasRef}
            data-testid="sos-card-qr-canvas"
            className="rounded-lg bg-white shadow-md"
            aria-label="Código QR de la tarjeta de emergencia"
          />
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            QR 100% local
          </span>
        </div>
      </div>

      <p className="text-[10px] text-emerald-300/80 mt-3 font-mono uppercase tracking-widest">
        + Una vez completada, ganarás +10 NexaTokens (una sola vez).
      </p>
    </div>
  );
}
